import {
  SupportTemplateCategory,
  SupportTemplateItem,
} from '../../../services/support-templates/support-templates.service';

/** Question chip row when the matcher surfaces item-level suggestions. */
export interface HelpBotFaqMatchedItem {
  category: SupportTemplateCategory;
  item: SupportTemplateItem;
}

export type HelpBotFaqMatchResult =
  | {
      type: 'answer';
      category: SupportTemplateCategory;
      item: SupportTemplateItem;
    }
  | {
      type: 'suggestions';
      categories?: SupportTemplateCategory[];
      items?: HelpBotFaqMatchedItem[];
    }
  | { type: 'none' };

interface ScoredItem {
  category: SupportTemplateCategory;
  item: SupportTemplateItem;
  score: number;
}

interface ScoredCategory {
  category: SupportTemplateCategory;
  score: number;
}

/** Ignore tiny filler words when measuring token overlap. */
const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'is',
  'are',
  'am',
  'was',
  'were',
  'be',
  'to',
  'of',
  'and',
  'or',
  'for',
  'in',
  'on',
  'at',
  'it',
  'do',
  'does',
  'did',
  'can',
  'could',
  'would',
  'should',
  'i',
  'me',
  'my',
  'you',
  'your',
  'we',
  'our',
  'with',
  'from',
  'this',
  'that',
  'please',
  'help',
  'need',
  'want',
  'about',
]);

const STRONG_SCORE = 0.72;
const SUGGEST_SCORE = 0.4;
const LEAD_GAP = 0.12;
const MAX_SUGGESTIONS = 8;

/** Lowercase, strip punctuation, collapse whitespace. */
export function normalizeHelpBotFaqText(text: string): string {
  return (text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text: string): string[] {
  return normalizeHelpBotFaqText(text)
    .split(' ')
    .filter((token) => token.length > 1 && !STOP_WORDS.has(token));
}

function uniqueTokens(text: string): string[] {
  return [...new Set(tokenize(text))];
}

/**
 * Score how well `query` matches `candidate`.
 * Prefers exact / containment matches, then query-token coverage.
 */
function scoreText(query: string, candidate: string): number {
  const q = normalizeHelpBotFaqText(query);
  const c = normalizeHelpBotFaqText(candidate);
  if (!q || !c) return 0;
  if (q === c) return 1;

  if (c.includes(q) && q.length >= 4) {
    return Math.min(0.96, 0.78 + q.length / Math.max(c.length, 1) * 0.18);
  }
  if (q.includes(c) && c.length >= 4) {
    return Math.min(0.94, 0.76 + c.length / Math.max(q.length, 1) * 0.18);
  }

  const qTokens = uniqueTokens(q);
  const cTokens = new Set(uniqueTokens(c));
  if (qTokens.length === 0 || cTokens.size === 0) return 0;

  let hits = 0;
  for (const token of qTokens) {
    if (cTokens.has(token)) {
      hits += 1;
      continue;
    }
    // Soft match: candidate token starts with / contains query token (typo-ish / stem).
    for (const candidateToken of cTokens) {
      if (
        candidateToken.startsWith(token) ||
        token.startsWith(candidateToken) ||
        (token.length >= 4 && candidateToken.includes(token))
      ) {
        hits += 0.7;
        break;
      }
    }
  }

  const coverage = hits / qTokens.length;
  if (coverage <= 0) return 0;

  // Reward denser overlap when the candidate is short (typical FAQ titles).
  const density = hits / Math.max(cTokens.size, 1);
  return Math.min(0.9, coverage * 0.78 + density * 0.18);
}

function scoreItem(
  query: string,
  category: SupportTemplateCategory,
  item: SupportTemplateItem,
): number {
  const questionScore = scoreText(query, item.question || '');
  const titleScore = scoreText(query, category.title || '') * 0.55;
  // Light answer keyword assist only when question/title already have some signal.
  const answerScore =
    questionScore >= 0.35 || titleScore >= 0.3
      ? scoreText(query, item.answer || '') * 0.2
      : 0;
  return Math.min(1, Math.max(questionScore, titleScore + questionScore * 0.35, answerScore));
}

function scoreCategory(query: string, category: SupportTemplateCategory): number {
  return scoreText(query, category.title || '');
}

/**
 * Match user home-composer text against already-loaded FAQ templates.
 * Strong → one FAQ answer; ambiguous → suggestion chips; none → escalate to admin.
 */
export function matchHelpBotFaq(
  userText: string,
  categories: SupportTemplateCategory[] | null | undefined,
): HelpBotFaqMatchResult {
  const query = (userText || '').trim();
  if (!query) return { type: 'none' };

  const list = Array.isArray(categories) ? categories : [];
  if (list.length === 0) return { type: 'none' };

  const scoredItems: ScoredItem[] = [];
  for (const category of list) {
    for (const item of category.items || []) {
      const score = scoreItem(query, category, item);
      if (score > 0) {
        scoredItems.push({ category, item, score });
      }
    }
  }
  scoredItems.sort((a, b) => b.score - a.score);

  const scoredCategories: ScoredCategory[] = list
    .map((category) => ({
      category,
      score: scoreCategory(query, category),
    }))
    .filter((row) => row.score >= SUGGEST_SCORE)
    .sort((a, b) => b.score - a.score);

  const best = scoredItems[0];
  const second = scoredItems[1];
  const bestCategory = scoredCategories[0];

  const queryTokens = uniqueTokens(query);
  const categoryTitleQuery =
    !!bestCategory &&
    bestCategory.score >= STRONG_SCORE &&
    queryTokens.length > 0 &&
    queryTokens.every((token) =>
      uniqueTokens(bestCategory.category.title).includes(token),
    );

  // Bare / near category title with several FAQs → question chips (do not auto-pick).
  if (
    categoryTitleQuery &&
    bestCategory.category.items.length > 1 &&
    !(best && best.score >= 0.95 && best.category.key === bestCategory.category.key)
  ) {
    return {
      type: 'suggestions',
      items: bestCategory.category.items
        .slice(0, MAX_SUGGESTIONS)
        .map((item) => ({
          category: bestCategory.category,
          item,
        })),
    };
  }

  const strongItem =
    !!best &&
    best.score >= STRONG_SCORE &&
    (!second || best.score - second.score >= LEAD_GAP || second.score < SUGGEST_SCORE);

  if (strongItem && best) {
    return {
      type: 'answer',
      category: best.category,
      item: best.item,
    };
  }

  // Strong category title with a single FAQ → treat as answer.
  if (
    bestCategory &&
    bestCategory.score >= STRONG_SCORE &&
    bestCategory.category.items.length === 1 &&
    (!best || best.category.key === bestCategory.category.key)
  ) {
    return {
      type: 'answer',
      category: bestCategory.category,
      item: bestCategory.category.items[0],
    };
  }

  const itemSuggestions = scoredItems
    .filter((row) => row.score >= SUGGEST_SCORE)
    .slice(0, MAX_SUGGESTIONS)
    .map(({ category, item }) => ({ category, item }));

  // Prefer question chips when we have item-level hits.
  if (itemSuggestions.length >= 2) {
    return { type: 'suggestions', items: itemSuggestions };
  }

  if (itemSuggestions.length === 1 && !strongItem) {
    // One middling hit — surface it as a chip rather than auto-answering.
    return { type: 'suggestions', items: itemSuggestions };
  }

  if (scoredCategories.length > 0) {
    // Category-only / title matches: show those topics (or their questions if one topic).
    if (
      scoredCategories.length === 1 &&
      scoredCategories[0].category.items.length > 1
    ) {
      return {
        type: 'suggestions',
        items: scoredCategories[0].category.items
          .slice(0, MAX_SUGGESTIONS)
          .map((item) => ({
            category: scoredCategories[0].category,
            item,
          })),
      };
    }
    return {
      type: 'suggestions',
      categories: scoredCategories
        .slice(0, MAX_SUGGESTIONS)
        .map((row) => row.category),
    };
  }

  return { type: 'none' };
}
